/**
 * Mirrors `Willnq.Domain.Modules.Identity.RoleNames` (backend). Keep in sync — these are the
 * exact string values carried in the JWT `roles` claim (ADR-0006).
 */
export const ROLES = {
  Candidate: 'Candidate',
  Recruiter: 'Recruiter',
  Company: 'Company',
  Vendor: 'Vendor',
  /**
   * **Super Admin.** The identifier stays `Admin` deliberately — renaming it would invalidate the
   * `roles` claim in every already-issued token (ADR-0032 §2). Display it as "Super Admin"; never
   * compare against that label.
   */
  Admin: 'Admin',
  PlatformAdmin: 'PlatformAdmin',
  Support: 'Support',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * The platform staff tiers, **most privileged first** — Super Admin ⊃ Platform Admin ⊃ Support
 * (ADR-0032 §1). A user holds at most one of these.
 *
 * Order matches the backend's `RoleNames.PlatformTiers` and is read by `platformTierOf`, so an
 * account that somehow holds two resolves to the more privileged.
 */
export const PLATFORM_TIERS: readonly Role[] = [ROLES.Admin, ROLES.PlatformAdmin, ROLES.Support];

/** Human labels for the tiers. `Admin` reads as "Super Admin" everywhere a person sees it. */
export const PLATFORM_TIER_LABELS: Record<string, string> = {
  [ROLES.Admin]: 'Super Admin',
  [ROLES.PlatformAdmin]: 'Platform Admin',
  [ROLES.Support]: 'Support',
};

/**
 * Roles for which MFA is mandatory (docs/business/BusinessRules.md Identity rule #2, RBAC §7-P2).
 * All three platform tiers are staff with cross-tenant reach, so all three require it.
 */
export const MFA_MANDATORY_ROLES: readonly Role[] = [
  ROLES.Admin,
  ROLES.PlatformAdmin,
  ROLES.Support,
  ROLES.Recruiter,
];

/**
 * The caller's platform tier, or `null` if they hold none. Where more than one is somehow present —
 * which the backend's grant command forbids, but a token issued before a revocation can still
 * carry — the **most privileged** wins, so a stale claim can never argue for less authority than the
 * account actually has.
 */
export function platformTierOf(roles: readonly string[] | undefined): Role | null {
  if (!roles) return null;
  return PLATFORM_TIERS.find((tier) => roles.includes(tier)) ?? null;
}
